import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import i18next from "i18next";

import { IPaginatedResponse } from "../../@types/PaginatedResponse";
import {
  IPatientJournals,
  PatientJournalsTableContent,
} from "../../@types/Patient";
import { DEFAULT_PAGE_SIZE } from "../../constants/pagination";
import { journalService } from "../../services/journalService";
import { getLongFormattedDate } from "../../utils/dateFormatter";

dayjs.extend(customParseFormat);

export function textTruncate(notesString: string) {
  if (!notesString) {
    return i18next.t("patientPage_journal_noText");
  }
  const length = 90;
  const ending = "...";

  if (notesString && notesString.length > length) {
    return notesString.substring(0, length - ending.length) + ending;
  }
  return notesString;
}

export function getPatientJournalsData(patientId: number) {
  const { data, isFetching, isError, refetch, isRefetching } =
    useQuery<IPatientJournals>({
      queryKey: [`patient-journals-table-${patientId}`, patientId],
      queryFn: async () => {
        if (!patientId) return {} as IPatientJournals;

        const { data } = await journalService.getJournals(patientId);
        return data as IPatientJournals;
      },
    });
  const patientJournals = data as IPatientJournals;

  return {
    patientJournalTable: patientJournals?.journals?.map((journalData) => {
      const date = getLongFormattedDate(journalData.date);
      return {
        ...journalData,
        tags: journalData.tags || "",
        notes: textTruncate(journalData.notes),
        date,
      } as PatientJournalsTableContent;
    }),
    journalsMaxDate: patientJournals?.maxDate,
    isFetching,
    isError,
    refetch,
    isRefetching,
  };
}

export function getJournalData(journalId: number) {
  const queryClient = useQueryClient();

  const { data, isFetching, isError, refetch, isRefetching } =
    useQuery<PatientJournalsTableContent>({
      queryKey: [`journal-${journalId}`, journalId],
      queryFn: async () => {
        if (!journalId) return {} as PatientJournalsTableContent;

        const { data } = await journalService.getJournalById(journalId);

        return data as PatientJournalsTableContent;
      },
    });

  const patientJournalData = data as PatientJournalsTableContent;

  const handleRemove = () => {
    queryClient.removeQueries({
      queryKey: [`journal-${journalId}`, journalId],
    });
  };

  return {
    patientJournalData,
    isFetching,
    isError,
    refetch,
    isRefetching,
    remove: handleRemove,
  };
}

// Type for the paginated journals response from the API
type JournalsPaginatedResponse = IPaginatedResponse<
  PatientJournalsTableContent[]
>;

/**
 * Hook for fetching patient journals with infinite scrolling support.
 * Uses TanStack Query's useInfiniteQuery for paginated data fetching.
 *
 * @param patientId - The patient ID to fetch journals for
 * @param pageSize - Number of items per page (default: 20)
 */
export function useInfiniteJournals(
  patientId: number,
  pageSize: number = DEFAULT_PAGE_SIZE
) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isRefetching,
  } = useInfiniteQuery({
    queryKey: ["patient-journals-infinite", patientId, pageSize],
    queryFn: async ({ pageParam }): Promise<JournalsPaginatedResponse> => {
      if (!patientId) {
        return {
          result: [],
          currentPage: 1,
          pageSize,
          pages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        };
      }

      const response = await journalService.getJournalsPaginated(
        patientId,
        pageParam,
        pageSize
      );

      // The API returns journals in data.result
      return response.data as JournalsPaginatedResponse;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: JournalsPaginatedResponse) => {
      if (lastPage.hasNextPage) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
  });

  // Flatten all pages into a single array of journals
  const journals =
    data?.pages.flatMap((page: JournalsPaginatedResponse) => {
      const items = page.result || [];
      return items.map((journalData: PatientJournalsTableContent) => {
        const date = getLongFormattedDate(journalData.date);
        return {
          ...journalData,
          tags: journalData.tags || "",
          notes: textTruncate(journalData.notes),
          date,
        } as PatientJournalsTableContent;
      });
    }) || [];

  // Get total count from the first page
  const totalCount =
    data?.pages[0]?.pages !== undefined
      ? data.pages[0].pages * pageSize
      : journals.length;

  return {
    journals,
    totalCount,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isRefetching,
  };
}
